/**
 * BotStudioAdminTab
 * Admin → Lucky Ducky → Bot Studio.
 * Backed by /api/admin/bot-studio/*.
 * Creates isolated TEST bots that hit the production spin path so we
 * exercise the real wallet/idempotency/ledger stack end-to-end without
 * contaminating live-player accounting (every ledger row tagged
 * meta.test_bot=true).
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Plus, Zap, Play, Trash2, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const int = (n) => Number(n || 0).toLocaleString();
const pct = (n) => `${Number(n || 0).toFixed(3)}%`;

const WAGERS = ['PLAY_1X', 'PLAY_2X', 'PLAY_3X', 'MAX_PLAY'];

export default function BotStudioAdminTab() {
    const [bots, setBots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newLabel, setNewLabel] = useState('');
    const [spinsForm, setSpinsForm] = useState({});   // { botId: {spins, wager} }
    const [depositForm, setDepositForm] = useState({});
    const [busyBotId, setBusyBotId] = useState(null);
    const [results, setResults] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/bot-studio/bots`, { withCredentials: true });
            setBots(data.bots || []);
        } catch (e) {
            toast.error('Failed to load bots', { description: e?.response?.data?.detail || e.message });
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const createBot = async () => {
        if (!newLabel.trim()) { toast.error('Enter a label'); return; }
        try {
            await axios.post(`${API}/admin/bot-studio/bots`, { label: newLabel.trim() }, { withCredentials: true });
            setNewLabel('');
            toast.success('Bot created');
            load();
        } catch (e) { toast.error('Create failed', { description: e?.response?.data?.detail || e.message }); }
    };
    const deposit = async (botId) => {
        const amt = Number(depositForm[botId] || 500);
        if (!(amt > 0)) return;
        setBusyBotId(botId);
        try {
            await axios.post(`${API}/admin/bot-studio/bots/${botId}/deposit`, { dollars: amt }, { withCredentials: true });
            toast.success(`Deposited ${money(amt)}`);
            load();
        } catch (e) { toast.error('Deposit failed', { description: e?.response?.data?.detail || e.message }); }
        finally { setBusyBotId(null); }
    };
    const playLd = async (botId) => {
        const spins = Number(spinsForm[botId]?.spins || 50);
        const wager_key = spinsForm[botId]?.wager || 'PLAY_1X';
        if (!(spins > 0)) return;
        setBusyBotId(botId);
        try {
            const { data } = await axios.post(`${API}/admin/bot-studio/bots/${botId}/play-ld`,
                { spins, wager_key }, { withCredentials: true, timeout: 120000 });
            if (data.mode === 'sync') {
                toast.success(`Ran ${data.summary.spins_completed} spins · RTP ${data.summary.rtp_pct}%`);
                setResults({ botId, summary: data.summary });
            } else {
                toast.info(`Dispatched ${data.spins_scheduled} async spins`);
            }
            load();
        } catch (e) { toast.error('Play failed', { description: e?.response?.data?.detail || e.message }); }
        finally { setBusyBotId(null); }
    };
    const resetBot = async (botId) => {
        if (!window.confirm('Reset bot wallet + clear all runs?')) return;
        setBusyBotId(botId);
        try {
            await axios.post(`${API}/admin/bot-studio/bots/${botId}/reset`, {}, { withCredentials: true });
            toast.success('Bot reset');
            load();
        } catch (e) { toast.error('Reset failed', { description: e?.response?.data?.detail || e.message }); }
        finally { setBusyBotId(null); }
    };
    const deleteBot = async (botId) => {
        if (!window.confirm('Soft-delete this bot?')) return;
        try {
            await axios.delete(`${API}/admin/bot-studio/bots/${botId}`, { withCredentials: true });
            toast.success('Bot deleted');
            load();
        } catch (e) { toast.error('Delete failed', { description: e?.response?.data?.detail || e.message }); }
    };

    return (
        <div className="space-y-6" data-testid="bot-studio">
            <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-white">Bot Studio</h2>
                <span className="px-2 py-0.5 rounded bg-amber-900/60 text-amber-200 text-xs uppercase tracking-wide">
                    TEST · Isolated · meta.test_bot=true
                </span>
                <button onClick={load} disabled={loading}
                        data-testid="bot-studio-refresh"
                        className="ml-auto p-2 rounded bg-slate-800/60 border border-slate-700 text-slate-200 hover:bg-slate-700">
                    <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                </button>
            </div>

            <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4 flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[240px]">
                    <label className="block text-xs uppercase text-slate-400 mb-1">New bot label</label>
                    <input
                        data-testid="bot-studio-new-label"
                        placeholder="e.g. P3 RTP smoke bot"
                        value={newLabel}
                        onChange={(e) => setNewLabel(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-slate-200" />
                </div>
                <button
                    data-testid="bot-studio-create"
                    onClick={createBot}
                    className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-400 text-black font-semibold flex items-center gap-2">
                    <Plus size={16} /> Create Bot
                </button>
            </div>

            <section className="rounded-lg border border-slate-700/60 bg-slate-900/40">
                <header className="px-4 py-3 border-b border-slate-700/60 text-slate-200 font-medium">
                    Test Bots ({bots.length})
                </header>
                <div className="divide-y divide-slate-800">
                    {bots.length === 0 && (
                        <div className="px-4 py-6 text-center text-slate-500">
                            No test bots. Create one above to start integration runs.
                        </div>
                    )}
                    {bots.map(b => (
                        <div key={b.id} className="p-4" data-testid={`bot-row-${b.id}`}>
                            <div className="flex flex-wrap items-center gap-3">
                                <div className="flex-1 min-w-[200px]">
                                    <div className="text-white font-medium">{b.label}</div>
                                    <div className="text-xs text-slate-400 font-mono">{b.email}</div>
                                </div>
                                <div className="text-sm">
                                    <span className="text-slate-400">Bal:</span> <strong className="text-green-400">{money(b.credits + b.bonus_credits)}</strong>
                                </div>
                                <div className="text-sm">
                                    <span className="text-slate-400">Wagered:</span> {money(b.total_wagered)}
                                </div>
                                <div className="text-sm">
                                    <span className="text-slate-400">Won:</span> {money(b.total_won)}
                                </div>
                                {b.last_run_summary && (
                                    <span className="ml-auto text-xs text-slate-400">
                                        last: {int(b.last_run_summary.spins_completed)} spins · RTP {pct(b.last_run_summary.rtp_pct)}
                                    </span>
                                )}
                            </div>
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                <input
                                    data-testid={`bot-deposit-${b.id}`}
                                    type="number" placeholder="$500"
                                    value={depositForm[b.id] ?? ''}
                                    onChange={(e) => setDepositForm(s => ({ ...s, [b.id]: e.target.value }))}
                                    className="w-24 px-2 py-1 bg-slate-800 border border-slate-700 rounded text-sm text-slate-200" />
                                <button
                                    data-testid={`bot-deposit-btn-${b.id}`}
                                    onClick={() => deposit(b.id)} disabled={busyBotId === b.id}
                                    className="px-3 py-1 text-sm rounded bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40">
                                    Fund
                                </button>
                                <span className="mx-2 text-slate-500">|</span>
                                <input
                                    data-testid={`bot-spins-${b.id}`}
                                    type="number" placeholder="50"
                                    value={spinsForm[b.id]?.spins ?? ''}
                                    onChange={(e) => setSpinsForm(s => ({ ...s, [b.id]: { ...(s[b.id] || {}), spins: e.target.value } }))}
                                    className="w-20 px-2 py-1 bg-slate-800 border border-slate-700 rounded text-sm text-slate-200" />
                                <select
                                    data-testid={`bot-wager-${b.id}`}
                                    value={spinsForm[b.id]?.wager || 'PLAY_1X'}
                                    onChange={(e) => setSpinsForm(s => ({ ...s, [b.id]: { ...(s[b.id] || {}), wager: e.target.value } }))}
                                    className="px-2 py-1 bg-slate-800 border border-slate-700 rounded text-sm text-slate-200">
                                    {WAGERS.map(w => <option key={w} value={w}>{w}</option>)}
                                </select>
                                <button
                                    data-testid={`bot-play-btn-${b.id}`}
                                    onClick={() => playLd(b.id)} disabled={busyBotId === b.id}
                                    className="px-3 py-1 text-sm rounded bg-green-600 hover:bg-green-500 text-white flex items-center gap-1 disabled:opacity-40">
                                    <Play size={12} /> Run Spins
                                </button>
                                <button
                                    data-testid={`bot-reset-btn-${b.id}`}
                                    onClick={() => resetBot(b.id)} disabled={busyBotId === b.id}
                                    className="ml-auto px-2 py-1 text-sm rounded bg-slate-700 hover:bg-slate-600 text-slate-200 flex items-center gap-1">
                                    <RotateCcw size={12} /> Reset
                                </button>
                                <button
                                    data-testid={`bot-delete-btn-${b.id}`}
                                    onClick={() => deleteBot(b.id)}
                                    className="px-2 py-1 text-sm rounded bg-red-900/60 hover:bg-red-800 text-red-200 flex items-center gap-1">
                                    <Trash2 size={12} /> Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {results && (
                <div className="rounded-lg border border-green-800/60 bg-green-950/30 p-4" data-testid="bot-last-run">
                    <div className="flex items-center gap-2 mb-2">
                        <Zap size={16} className="text-green-400" />
                        <span className="text-white font-medium">Last Run · Bot {results.botId.slice(-6)}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-slate-200">
                        <div><span className="text-slate-400">Spins:</span> {int(results.summary.spins_completed)}/{int(results.summary.spins_requested)}</div>
                        <div><span className="text-slate-400">Wagered:</span> {int(results.summary.wagered_credits)}c</div>
                        <div><span className="text-slate-400">Won:</span> {int(results.summary.won_credits)}c</div>
                        <div><span className="text-slate-400">RTP:</span> {pct(results.summary.rtp_pct)}</div>
                        <div><span className="text-slate-400">Features:</span> {results.summary.features}</div>
                    </div>
                </div>
            )}
        </div>
    );
}
